import type { Ref } from 'react';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { NetworkRequest } from 'c-data-layer';
import { callApi, NetworkRequestState } from 'c-data-layer';
import { useDispatch } from 'react-redux';
import type {
    ChartReportProps,
    ReportDateSettings,
    ReportWrapperProps,
    ReportWrapperRefAPI,
    TableReportProps,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType, ReportWeekDay } from 'c-reports/Types';
import { Box } from '@mui/material';
import { LoadingSpinner } from 'c-components';
import ReportTableWrapper from '../ReportTableWrapper';
import ReportDateSwitcher from '../ReportDateSwitcher';
import ReportControlsWrapper from '../ReportControlsWrapper';
import ReportChartWrapper from '../ReportChartWrapper';
import { figureOutEndDate, figureOutStartDate, generateRequestKey } from './reportDates';

type Props<DataStructure> = ReportWrapperProps<DataStructure> & {
    removeLoadingBar?: boolean;
    onResetAdvancedFilters?: () => void;
    filterExtras?: React.ReactNode[];
    externalLoading?: boolean;
    /** Centred in the date/filter row, between the date controls and the filters. */
    centerControls?: React.ReactNode;
};

function ReportWrapper<DataStructure>(
    {
        initialStartDate,
        initialEndDate,
        fetchData,
        onDataUpdated,
        dateSettings,
        wrapperProps,
        reportWrapperProps,
        reportChartWrapperProps,
        additionalDateControls,
        additionalDateControlsPosition,
        beforeChartComponent,
        afterChartComponent,
        reportProps,
        ReportRender,
        onLoadingStateUpdated,
        requestKeyPrefix = '',
        removeLoadingBar = false,
        onResetAdvancedFilters,
        filterExtras = [],
        centerControls,
        externalLoading = false,
    }: Props<DataStructure>,
    ref: Ref<ReportWrapperRefAPI>,
) {
    const dispatch = useDispatch();
    const [timeframe, setTimeframe] = useState<ReportDateTimeframe>(
        dateSettings?.timeframe ?? ReportDateTimeframe.Weekly,
    );

    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const actualDateSettings = useMemo<ReportDateSettings>(() => {
        const defaults: ReportDateSettings = {
            timeframe,
            minDate: initialStartDate ?? new Date(),
            maxDate: initialEndDate ?? new Date(),
            firstDayOfWeek: ReportWeekDay.Monday,
        };

        return { ...defaults, ...dateSettings, timeframe };
    }, [dateSettings, initialStartDate, initialEndDate, timeframe]);

    const [startDate, setStartDate] = useState(
        figureOutStartDate(actualDateSettings, initialStartDate),
    );
    const [endDate, setEndDate] = useState(
        figureOutEndDate(actualDateSettings, initialEndDate, startDate),
    );

    const [loading, setLoading] = useState<Record<string, NetworkRequest>>({});
    const [data, setData] = useState<Record<string, any>>({});
    const currentKey = useMemo(
        () => generateRequestKey(requestKeyPrefix, startDate, endDate, timeframe),
        [startDate, endDate, timeframe, requestKeyPrefix],
    );

    const abortControllerRef = useRef<AbortController>();

    const getData = useCallback(
        async (newTimeframe: ReportDateTimeframe, start?: Date, end?: Date) => {
            const requestStartDate = start ?? startDate;
            const requestEndDate = end ?? endDate;
            const requestKey = generateRequestKey(
                requestKeyPrefix,
                requestStartDate,
                requestEndDate,
                newTimeframe,
            );

            if (
                loading[requestKey]?.state === NetworkRequestState.InProgress &&
                abortControllerRef?.current != null
            ) {
                abortControllerRef.current.abort();
            }

            setLoading({
                ...loading,
                [requestKey]: { state: NetworkRequestState.InProgress },
            });

            const { request, controller } = fetchData(
                requestStartDate,
                requestEndDate,
                newTimeframe,
            );

            abortControllerRef.current = controller;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const [error, responseData] = await dispatch(callApi(request));

            if (mountedRef.current === false) {
                return;
            }

            if (!error) {
                setData({ ...data, [requestKey]: responseData });

                setTimeout(() => {
                    setLoading({
                        ...loading,
                        [requestKey]: { state: NetworkRequestState.Success },
                    });
                }, 10);
            } else {
                setLoading({
                    ...loading,
                    [requestKey]: { state: NetworkRequestState.Error },
                });
            }
        },
        [startDate, endDate, requestKeyPrefix, loading, dispatch, fetchData, data],
    );

    const onDateChange = useCallback(
        (timeframe: ReportDateTimeframe, start: Date, end: Date, calledFromRef = false) => {
            setStartDate(start);
            setEndDate(end);

            if (!calledFromRef) {
                actualDateSettings?.onDatesUpdated?.(timeframe, start, end);
            }

            getData(timeframe, start, end);
        },
        [getData, actualDateSettings],
    );

    const requestState = useMemo(() => {
        const internalState = loading[currentKey]?.state ?? NetworkRequestState.Idle;
        if (externalLoading && internalState !== NetworkRequestState.InProgress) {
            return NetworkRequestState.InProgress;
        }
        return internalState;
    }, [currentKey, loading, externalLoading]);

    const currentData = useMemo(() => data[currentKey] ?? null, [currentKey, data]);

    useEffect(
        () => {
            onDataUpdated?.(currentData, startDate, endDate, timeframe);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentData, onDataUpdated],
    );

    useEffect(() => {
        if (onLoadingStateUpdated) {
            onLoadingStateUpdated(loading[currentKey]);
        }
    }, [onLoadingStateUpdated, loading, currentKey]);

    useEffect(() => {
        getData(timeframe);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSwitchTimeframe = useCallback(
        ($timeframe: ReportDateTimeframe, start?: Date, end?: Date, calledFromRef = false) => {
            if ($timeframe === ReportDateTimeframe.All) {
                onDateChange(
                    $timeframe,
                    actualDateSettings.minDate,
                    actualDateSettings.maxDate,
                    calledFromRef,
                );
            } else {
                const newDateSettings = { ...actualDateSettings, timeframe: $timeframe };
                const newStartDate = figureOutStartDate(newDateSettings, start ?? startDate);
                const newEndDate = figureOutEndDate(
                    newDateSettings,
                    end ?? endDate,
                    start ?? startDate,
                );
                onDateChange($timeframe, newStartDate, newEndDate, calledFromRef);
            }
            setTimeframe($timeframe);
        },
        [actualDateSettings, endDate, onDateChange, startDate],
    );

    const refreshData = useCallback(() => getData(timeframe), [getData, timeframe]);
    const refreshDataRemoveFilters = useCallback(() => {
        onResetAdvancedFilters?.();
        getData(timeframe);
    }, [getData, timeframe, onResetAdvancedFilters]);
    /**
     * provide a custom ref (useRef) API
     */
    useImperativeHandle(
        ref,
        () => ({
            refreshData,
            setDates: (timeframe, startDate, endDate) => {
                onSwitchTimeframe(timeframe, startDate, endDate, true);
            },
        }),
        [refreshData, onSwitchTimeframe],
    );

    const childReports = useMemo(
        () =>
            reportProps
                ?.map(({ RenderReport, ...rep }, ri) => {
                    if (RenderReport != null) {
                        // eslint-disable-next-line react/no-array-index-key
                        return <RenderReport {...rep} key={ri} />;
                    }
                    if (rep.type === ReportType.Table) {
                        return (
                            <ReportControlsWrapper
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${rep.type}-${ri}`}
                                tableProps={{ ...(rep as TableReportProps<DataStructure>) }}
                                type={rep.type}
                                namePrefix={rep.namePrefix}
                                startDate={startDate}
                                endDate={endDate}
                            >
                                <ReportTableWrapper
                                    addLoadingBar={removeLoadingBar}
                                    {...(rep as TableReportProps<DataStructure>)}
                                    loadingState={requestState}
                                    refreshData={refreshDataRemoveFilters}
                                    onRetry={refreshData}
                                />
                            </ReportControlsWrapper>
                        );
                    }
                    if (
                        [ReportType.Line, ReportType.Bar, ReportType.Pie, ReportType.Mixed].indexOf(
                            rep.type,
                        ) !== -1
                    ) {
                        return (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${rep.type}-${ri}`}
                                id={rep.namePrefix.replace(/^\d+_/, '')}
                                height={160}
                                width="100%"
                                {...reportChartWrapperProps}
                            >
                                <ReportControlsWrapper
                                    chartProps={{ ...(rep as ChartReportProps<DataStructure>) }}
                                    type={rep.type}
                                    namePrefix={rep.namePrefix}
                                    startDate={startDate}
                                    endDate={endDate}
                                >
                                    <ReportChartWrapper
                                        {...(rep as ChartReportProps<DataStructure>)}
                                        type={rep.type}
                                        loadingState={requestState}
                                        onRetry={refreshData}
                                        chartProps={{
                                            margin: { top: 20, right: 30, left: 65, bottom: 20 },
                                        }}
                                    />
                                </ReportControlsWrapper>
                            </Box>
                        );
                    }

                    return null;
                })
                .filter(rep => rep != null),
        [
            endDate,
            refreshData,
            refreshDataRemoveFilters,
            removeLoadingBar,
            reportChartWrapperProps,
            reportProps,
            requestState,
            startDate,
        ],
    );

    const reports = useMemo(
        () =>
            ReportRender ? (
                <ReportRender reports={childReports} />
            ) : (
                childReports?.map((rep, ri) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`${rep.type}-${ri}`}>{rep}</React.Fragment>
                )) ?? []
            ),
        [ReportRender, childReports],
    );

    return (
        <Box {...wrapperProps}>
            {actualDateSettings.timeframe != null && (
                <Box
                    sx={{
                        display: 'flex',
                        py: 1,
                        ...(centerControls ? { position: 'relative', flexWrap: 'wrap' } : {}),
                    }}
                >
                    {additionalDateControls &&
                        additionalDateControlsPosition === 'start' &&
                        additionalDateControls}
                    <ReportDateSwitcher
                        startDate={startDate}
                        endDate={endDate}
                        onChange={onDateChange}
                        {...actualDateSettings}
                        onSwitchTimeframe={onSwitchTimeframe}
                        timeframe={timeframe}
                    />
                    {filterExtras ?? filterExtras.map(filter => filter)}
                    {additionalDateControls &&
                        additionalDateControlsPosition === 'end' &&
                        additionalDateControls}
                    {centerControls && (
                        // Centred on the row itself rather than in the gap between its
                        // neighbours, so it lines up under the tab regardless of their widths.
                        // Narrow screens give it a line of its own instead.
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: { xs: '100%', md: 'auto' },
                                order: { xs: -1, md: 0 },
                                pb: { xs: 1, md: 0 },
                                position: { md: 'absolute' },
                                top: { md: '50%' },
                                left: { md: '50%' },
                                transform: { md: 'translate(-50%, -50%)' },
                            }}
                        >
                            {centerControls}
                        </Box>
                    )}
                </Box>
            )}
            {beforeChartComponent && beforeChartComponent}
            <Box position="relative" minHeight={320} {...reportWrapperProps}>
                {requestState === NetworkRequestState.InProgress && !removeLoadingBar && (
                    <Box sx={{ position: 'absolute', top: 100, left: '50%', zIndex: 1 }}>
                        <LoadingSpinner />
                    </Box>
                )}
                {reports}
            </Box>
            {afterChartComponent && afterChartComponent}
        </Box>
    );
}

export default React.forwardRef(ReportWrapper);
